import {
  BookingStatusFilter,
  BookingStatusFilterEnum,
  BookingWithDetail,
  CreateBooking
} from "@garther/interfaces";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClients } from "./context";

export const bookingsKeys = {
  all: ["bookings"] as const,
  lists: () => [...bookingsKeys.all, "list"] as const,
  list: () => [...bookingsKeys.lists(), "my"] as const,
  details: () => [...bookingsKeys.all, "detail"] as const,
  detail: (bookingId: string) => [...bookingsKeys.details(), bookingId] as const,
  tickets: (bookingId: string) => [...bookingsKeys.all, bookingId, "tickets"] as const,
  byEvent: (eventId: string) => [...bookingsKeys.all, "event", eventId] as const
};

interface UseMyBookingsFilterOptions {
  status?: BookingStatusFilter;
  enabled?: boolean;
}

export function useMyBookings(options: UseMyBookingsFilterOptions = {}) {
  const { bookingApiClient } = useApiClients();
  const { status = BookingStatusFilterEnum.ALL, enabled = true } = options;

  return useQuery({
    queryKey: [...bookingsKeys.list(), status],
    queryFn: () => bookingApiClient.getMyBookings({ status }),
    enabled,
    staleTime: 5 * 60 * 1000
  });
}

export function useBooking(options: { bookingId: string; enabled?: boolean }) {
  const { bookingApiClient } = useApiClients();
  const { bookingId, enabled = true } = options;

  return useQuery({
    queryKey: bookingsKeys.detail(bookingId),
    queryFn: () => bookingApiClient.getBookingById(bookingId),
    enabled: enabled && !!bookingId,
    staleTime: 5 * 60 * 1000
  });
}

export function useBookingTickets(options: { bookingId: string; enabled?: boolean }) {
  const { ticketApiClient } = useApiClients();
  const { bookingId, enabled = true } = options;

  return useQuery({
    queryKey: bookingsKeys.tickets(bookingId),
    queryFn: () => ticketApiClient.getList({ bookingId, page: 1, limit: 100 }),
    enabled: enabled && !!bookingId,
    staleTime: 5 * 60 * 1000
  });
}

export function useBookingsInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: bookingsKeys.all }),
    invalidateList: () => queryClient.invalidateQueries({ queryKey: bookingsKeys.lists() }),
    invalidateMyBookings: () => queryClient.invalidateQueries({ queryKey: bookingsKeys.list() }),
    invalidateBooking: (bookingId: string) =>
      queryClient.invalidateQueries({ queryKey: bookingsKeys.detail(bookingId) }),
    invalidateBookingTickets: (bookingId: string) =>
      queryClient.invalidateQueries({ queryKey: bookingsKeys.tickets(bookingId) })
  };
}

export function useCreateBookingMutation(
  options: { onSuccess?: (result: BookingWithDetail) => void; onError?: (error: unknown) => void } = {}
) {
  const { bookingApiClient } = useApiClients();
  return useMutation({
    mutationFn: (data: CreateBooking) => bookingApiClient.book(data),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error) => options.onError?.(error)
  });
}

export function useBookingsByEvent(eventId: string, options: { enabled?: boolean } = {}) {
  const { bookingApiClient } = useApiClients();

  return useQuery({
    queryKey: bookingsKeys.byEvent(eventId),
    queryFn: () => bookingApiClient.getBookingsByEventId(eventId),
    enabled: (options.enabled ?? true) && !!eventId,
    staleTime: 5 * 60 * 1000
  });
}
